import { DOCUMENT_TYPE_CODES } from '../../../constants/documentTypeCodes';
import { ActionItem } from '../../../types/button/ActionButton';

const isExcludedSubject = (row: { documentCode?: string }) =>
  row?.documentCode === DOCUMENT_TYPE_CODES.EXCLUDED_SUBJECT;

export const getPurchaseTableActions = ({
  row,
  canCreateReturn = false,
  canResendDte = false,
  handlers,
}: {
  row: {
    id: number;
    status: 'PENDING' | 'DRAFT_CANCELLED' | 'FINALIZED' | 'SETTLED' | 'CANCELED' | 'CREDITED' | 'DTE_PENDING' | 'DTE_SENT' | 'DTE_PROCESSED' | 'DTE_REJECTED' | 'REJECTED';
    documentCode?: string;
    hasDteError?: boolean;
    hasHaciendaResponse?: boolean;
  };
  /** Evento de Retorno: solo ADMIN y SUPPORT pueden generarlo. */
  canCreateReturn?: boolean;
  /** Reenviar DTE de una compra rechazada: solo ADMIN y SUPPORT. */
  canResendDte?: boolean;
  handlers: {
    onView: (id: number, isCancelled?: boolean) => void;
    onCancel: (id: number) => void;
    onCancelPending: (id: number) => void;
    onContinue: (id: number) => void;
    onDownloadPdf: (id: number) => void;
    onConsultDTE: (id: number) => void;
    onExportJson: (purchaseId: number) => void;
    onViewInvalidDte: (id: number) => void;
    onForceAnnulRejectedPurchase: (id: number) => void;
    onSendEmail: (id: number) => void;
    onViewDteError: (id: number) => void;
    onCreateReturn: (id: number) => void;
    onResendDTE: (id: number) => void;
  };
}): ActionItem[] => {
  const actions: ActionItem[] = [];
  if (row.hasHaciendaResponse || row.hasDteError) actions.push({
    // 'dte-error' = rojo (rechazo); 'dte-response' = verde (respuesta OK) — igual que en ventas.
    key: row.hasDteError ? 'dte-error' : 'dte-response',
    label: row.hasDteError ? 'Ver rechazo de Hacienda' : 'Ver respuesta de Hacienda',
    onClick: () => handlers.onViewDteError(row.id),
  });

  const addStandardExcludedSubjectActions = (id: number) => {
    actions.push(
      { key: 'pdf', label: 'PDF', onClick: () => handlers.onDownloadPdf(id) },
      { key: 'dte', label: 'Consultar DTE', onClick: () => handlers.onConsultDTE(id) },
      { key: 'json', label: 'Descargar JSON', onClick: () => handlers.onExportJson(row.id) },
    );
  };

  // 🟡 PENDING
  if (row.status === 'PENDING') {
    actions.push(
      { key: 'continue', label: 'Continuar', onClick: () => handlers.onContinue(row.id) },
      { key: 'cancel-pending', label: 'Cancelar compra', onClick: () => handlers.onCancelPending(row.id) },
    );
    return actions;
  }

  // ⚪ DRAFT_CANCELLED (cancelada en pendiente)
  if (row.status === 'DRAFT_CANCELLED') {
    actions.push({ key: 'view', label: 'Ver Compra', onClick: () => handlers.onView(row.id, true) });
    return actions;
  }

  // 🟤 DTE_PENDING → Ver, PDF, Consultar DTE
  if (row.status === 'DTE_PENDING' && isExcludedSubject(row)) {
    actions.push({ key: 'view', label: 'Ver Compra', onClick: () => handlers.onView(row.id) });
    addStandardExcludedSubjectActions(row.id);
    return actions;
  }

  // 🟦 DTE_SENT → igual que DTE_PENDING
  if (row.status === 'DTE_SENT' && isExcludedSubject(row)) {
    actions.push({ key: 'view', label: 'Ver Compra', onClick: () => handlers.onView(row.id) });
    addStandardExcludedSubjectActions(row.id);
    return actions;
  }

  // 🟢 DTE_PROCESSED (ya autorizado y oficial)
  if (row.status === 'DTE_PROCESSED' && isExcludedSubject(row)) {
    actions.push(
      { key: 'view', label: 'Ver Compra', onClick: () => handlers.onView(row.id) },
      { key: 'cancel', label: 'Anular Compra', onClick: () => handlers.onCancel(row.id) },
      { key: 'pdf', label: 'PDF', onClick: () => handlers.onDownloadPdf(row.id) },
      { key: 'dte', label: 'Consultar DTE', onClick: () => handlers.onConsultDTE(row.id) },
      { key: 'json', label: 'Descargar JSON', onClick: () => handlers.onExportJson(row.id) },
      { key: 'email', label: 'Enviar DTE por correo', onClick: () => handlers.onSendEmail(row.id) },
    );
    if (canCreateReturn) {
      actions.push({ key: 'return-event', label: 'Crear evento de retorno', onClick: () => handlers.onCreateReturn(row.id) });
    }
    return actions;
  }

  // 🟢 FINALIZED o SETTLED (flujo normal)
  if (row.status === 'FINALIZED' || row.status === 'SETTLED') {
    actions.push(
      { key: 'view', label: 'Ver Compra', onClick: () => handlers.onView(row.id) },
      { key: 'cancel', label: 'Anular Compra', onClick: () => handlers.onCancel(row.id) },
    );

    if (isExcludedSubject(row)) {
      addStandardExcludedSubjectActions(row.id);
      actions.push({ key: 'email', label: 'Enviar DTE por correo', onClick: () => handlers.onSendEmail(row.id) });
      if (canCreateReturn) {
        actions.push({ key: 'return-event', label: 'Crear evento de retorno', onClick: () => handlers.onCreateReturn(row.id) });
      }
    }
  }

  // 🔵 CREDITED (Nota de crédito aplicada)
  if (row.status === 'CREDITED') {
    actions.push(
      { key: 'view', label: 'Ver Nota de Crédito', onClick: () => handlers.onView(row.id) },
      { key: 'cancel', label: 'Anular Nota de Crédito', onClick: () => handlers.onCancel(row.id) },
    );

    if (isExcludedSubject(row)) {
      addStandardExcludedSubjectActions(row.id);
    }
  }

  // 🔴 CANCELED
  if (row.status === 'CANCELED') {
    actions.push({ key: 'view', label: 'Ver Compra Anulada', onClick: () => handlers.onView(row.id, true) });

    if (isExcludedSubject(row)) {
      actions.push(
        { key: 'cancel', label: 'Ver DTE Invalidado', onClick: () => handlers.onViewInvalidDte(row.id) },
        { key: 'dte', label: 'Consultar DTE', onClick: () => handlers.onConsultDTE(row.id) }
      );
    }
  }

  // ❌ DTE_REJECTED → Permitir ver, consultar, reenviar y anulación técnica
  if (row.status === 'DTE_REJECTED') {
    actions.push(
      {
        key: 'view',
        label: 'Ver Compra (Rechazada)',
        onClick: () => handlers.onView(row.id),
      },
      {
        key: 'dte',
        label: 'Consultar DTE',
        onClick: () => handlers.onConsultDTE(row.id),
      },
    );

    if (canResendDte) {
      actions.push({
        key: 'resend',
        label: 'Reenviar DTE',
        onClick: () => handlers.onResendDTE(row.id),
      });
    }

    actions.push({
      key: 'cancel',
      label: 'Anulación Técnica',
      onClick: () => handlers.onForceAnnulRejectedPurchase(row.id),
    });

    return actions;
  }

  // 🚫 REJECTED → Solo permitir ver la compra también
  if (row.status === 'REJECTED') {
    actions.push({
      key: 'view',
      label: 'Ver Compra (Rechazada)',
      onClick: () => handlers.onView(row.id),
    });
    return actions;
  }

  return actions;
};
